import { useState, useEffect } from "react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Guinea regions data with SVG paths (simplified representation)
const GUINEA_REGIONS = {
  "Conakry": {
    path: "M 145 380 L 160 370 L 175 380 L 170 395 L 150 395 Z",
    center: { x: 160, y: 382 },
  },
  "Kindia": {
    path: "M 100 320 L 180 300 L 200 340 L 180 380 L 145 380 L 120 360 Z",
    center: { x: 150, y: 340 },
  },
  "Boké": {
    path: "M 50 200 L 120 180 L 150 220 L 140 280 L 100 320 L 60 300 L 40 250 Z",
    center: { x: 95, y: 250 },
  },
  "Mamou": {
    path: "M 180 300 L 250 280 L 280 320 L 260 360 L 200 340 Z",
    center: { x: 230, y: 320 },
  },
  "Labé": {
    path: "M 120 180 L 200 150 L 250 180 L 250 280 L 180 300 L 150 220 Z",
    center: { x: 190, y: 220 },
  },
  "Faranah": {
    path: "M 250 280 L 350 260 L 380 320 L 360 380 L 280 360 L 280 320 Z",
    center: { x: 315, y: 320 },
  },
  "Kankan": {
    path: "M 350 260 L 450 220 L 500 280 L 480 360 L 400 380 L 380 320 Z",
    center: { x: 425, y: 300 },
  },
  "Nzérékoré": {
    path: "M 280 360 L 360 380 L 400 380 L 420 450 L 350 480 L 280 450 L 260 400 Z",
    center: { x: 340, y: 420 },
  }
};

export default function GuineaMap({ regionsStats = [], onRegionClick, selectedRegion: propSelectedRegion }) {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(propSelectedRegion);
  const [regionStats, setRegionStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Sync with prop
  useEffect(() => {
    setSelectedRegion(propSelectedRegion);
  }, [propSelectedRegion]);

  useEffect(() => {
    // Use provided regionsStats or fetch from API
    if (regionsStats && regionsStats.length > 0) {
      const statsMap = {};
      regionsStats.forEach(r => {
        statsMap[r.region] = {
          total_enterprises: r.total_entreprises || 0,
          total_bilans: (r.bilans_soumis || 0) + (r.bilans_valides || 0) + (r.bilans_rejetes || 0),
          bilans_valides: r.bilans_valides || 0,
          bilans_en_attente: r.bilans_soumis || 0,
          bilans_rejetes: r.bilans_rejetes || 0,
          conformite: r.conformite || 'vert'
        };
      });
      setRegionStats(statsMap);
      setLoading(false);
    } else {
      fetchRegionStats();
    }
  }, [regionsStats]);

  const fetchRegionStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/statistics/by-region`);
      setRegionStats(response.data);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch region stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRegionColor = (region) => {
    const stat = regionStats[region];
    if (!stat) return "#e5e7eb";
    
    // Use conformite if available
    if (stat.conformite) {
      if (stat.conformite === 'vert') return "#34d399";
      if (stat.conformite === 'jaune') return "#fbbf24";
      if (stat.conformite === 'rouge') return "#f87171";
    }
    
    const count = stat.total_bilans || 0;
    if (count === 0) return "#f3f4f6";
    if (count < 5) return "#d1fae5";
    if (count < 10) return "#6ee7b7";
    if (count < 20) return "#34d399";
    if (count < 50) return "#10b981";
    return "#059669";
  };

  const handleRegionClick = (region) => {
    setSelectedRegion(region === selectedRegion ? null : region);
    if (onRegionClick) {
      onRegionClick(region);
    }
  };

  return (
    <div className="relative">
      <svg
        viewBox="0 0 550 520"
        className="w-full h-auto"
        style={{ maxHeight: "400px" }}
      >
        {/* Background */}
        <rect x="0" y="0" width="550" height="520" fill="#f8fafc" rx="8" />
        
        {/* Title */}
        <text x="275" y="35" textAnchor="middle" className="font-bold" style={{ fontSize: "14px", fill: "#1e293b" }}>
          Répartition Géographique des Bilans Sociaux
        </text>
        
        {/* Ocean indicator */}
        <text x="25" y="400" style={{ fontSize: "9px", fill: "#93c5fd" }}>
          Océan
        </text>
        <text x="25" y="412" style={{ fontSize: "9px", fill: "#93c5fd" }}>
          Atlantique
        </text>
        
        {/* Regions */}
        {Object.entries(GUINEA_REGIONS).map(([region, data]) => {
          const isHovered = hoveredRegion === region;
          const isSelected = selectedRegion === region;
          const stat = regionStats[region] || {};
          
          return (
            <g key={region}>
              {/* Region shape */}
              <path
                d={data.path}
                fill={getRegionColor(region)}
                stroke={isSelected ? "#1B4D3E" : isHovered ? "#059669" : "#ffffff"}
                strokeWidth={isSelected ? 3 : isHovered ? 2 : 1.5}
                className="cursor-pointer transition-all duration-200 hover:brightness-110"
                onMouseEnter={() => setHoveredRegion(region)}
                onMouseLeave={() => setHoveredRegion(null)}
                onClick={() => handleRegionClick(region)}
              />
              
              {/* Region label */}
              <text
                x={data.center.x}
                y={data.center.y - 8}
                textAnchor="middle"
                className="pointer-events-none font-semibold"
                style={{ fontSize: "10px", fill: "#1f2937" }}
              >
                {region}
              </text>
              
              {/* Stats count */}
              <text
                x={data.center.x}
                y={data.center.y + 6}
                textAnchor="middle"
                className="pointer-events-none"
                style={{ fontSize: "9px", fill: "#4b5563" }}
              >
                {stat.total_bilans || 0} bilans
              </text>
            </g>
          );
        })}
        
        {/* Legend */}
        <g transform="translate(380, 470)">
          <text x="0" y="0" style={{ fontSize: "9px", fill: "#64748b", fontWeight: "600" }}>
            Conformité:
          </text>
          <rect x="0" y="8" width="16" height="10" fill="#34d399" stroke="#d1d5db" strokeWidth="0.5" rx="2" />
          <text x="20" y="16" style={{ fontSize: "8px", fill: "#6b7280" }}>Bon</text>
          <rect x="50" y="8" width="16" height="10" fill="#fbbf24" stroke="#d1d5db" strokeWidth="0.5" rx="2" />
          <text x="70" y="16" style={{ fontSize: "8px", fill: "#6b7280" }}>Moyen</text>
          <rect x="105" y="8" width="16" height="10" fill="#f87171" stroke="#d1d5db" strokeWidth="0.5" rx="2" />
          <text x="125" y="16" style={{ fontSize: "8px", fill: "#6b7280" }}>Faible</text>
        </g>
      </svg>
      
      {/* Tooltip on hover */}
      {hoveredRegion && (
        <div className="absolute top-10 right-2 bg-white shadow-xl rounded-lg p-3 border border-gray-200 min-w-[200px] z-10">
          <h3 className="font-bold text-base text-primary border-b pb-2 mb-2">{hoveredRegion}</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Entreprises:</span>
              <span className="font-semibold bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                {regionStats[hoveredRegion]?.total_enterprises || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Bilans total:</span>
              <span className="font-semibold bg-gray-100 text-gray-800 px-2 py-0.5 rounded text-xs">
                {regionStats[hoveredRegion]?.total_bilans || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Validés:</span>
              <span className="font-semibold bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                {regionStats[hoveredRegion]?.bilans_valides || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">En attente:</span>
              <span className="font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs">
                {regionStats[hoveredRegion]?.bilans_en_attente || 0}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
